import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { AlertTriangle, ArrowLeft, ArrowRight, ImagePlus, Trash2, Upload } from "lucide-react";

const BUCKET = "club-media";
const MAX_SPONSORS = 6;
const SPONSOR_MAX_BYTES = 5 * 1024 * 1024;
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const SPONSOR_TYPES = ["image/png", "image/jpeg"];
const PHOTO_TYPES = ["image/png", "image/jpeg"];
// Der Bucket ist privat; Dateien werden öffentlich über diese Funktion ausgeliefert.
const FILE_ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/club-media-file`;

async function isReachable(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, { cache: "no-store" });
    return res.status === 200 && (res.headers.get("content-type") ?? "").startsWith("image/");
  } catch {
    return false;
  }
}

async function resizeImage(file: File, maxWidth: number, isPng: boolean): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  if (bitmap.width <= maxWidth) {
    bitmap.close();
    return file;
  }
  const scale = maxWidth / bitmap.width;
  const canvas = document.createElement("canvas");
  canvas.width = maxWidth;
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Bild konnte nicht verkleinert werden."))),
      isPng ? "image/png" : "image/jpeg",
      0.9,
    ),
  );
}

type PhotoKey = "photo_matchday" | "photo_goal" | "photo_final";

const PHOTO_SLOTS: { key: PhotoKey; title: string; hint: string }[] = [
  { key: "photo_matchday", title: "Spielankündigung", hint: "z. B. euer Platz oder die Mannschaft" },
  { key: "photo_goal", title: "Tor-Alert", hint: "z. B. Jubelbild" },
  { key: "photo_final", title: "Endstand", hint: "z. B. Mannschaft nach dem Spiel" },
];

interface Props {
  clubId: string;
  clubName: string;
  userId: string;
  sponsorUrls: string[];
  sponsorsEnabled: boolean;
  photos: Record<PhotoKey, string | null>;
}

export default function ClubMediaSection({
  clubId,
  clubName,
  userId,
  sponsorUrls,
  sponsorsEnabled,
  photos,
}: Props) {
  const { toast } = useToast();
  const [sponsors, setSponsors] = useState<string[]>(sponsorUrls ?? []);
  const [showSponsors, setShowSponsors] = useState<boolean>(sponsorsEnabled);
  const [photoUrls, setPhotoUrls] = useState<Record<PhotoKey, string | null>>(photos);
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState<Set<string>>(new Set());
  const sponsorInput = useRef<HTMLInputElement>(null);

  // Beim Laden prüfen, ob hinterlegte Adressen wirklich erreichbar sind.
  useEffect(() => {
    const urls = [...(sponsorUrls ?? []), ...Object.values(photos).filter(Boolean)] as string[];
    if (!urls.length) return;
    let cancelled = false;
    Promise.all(urls.map(async (u) => [u, await isReachable(u)] as const)).then((res) => {
      if (!cancelled) setBroken(new Set(res.filter(([, ok]) => !ok).map(([u]) => u)));
    });
    return () => {
      cancelled = true;
    };
  }, [sponsorUrls, photos]);

  const publicUrl = (path: string) => `${FILE_ENDPOINT}?path=${encodeURIComponent(path)}`;

  const notifySaved = () =>
    toast({ title: "Gespeichert", description: "Gespeichert – ab dem nächsten Spiel sind eure Logos dabei." });

  const persist = async (
    nextSponsors: string[],
    nextShow: boolean,
    nextPhotos: Record<PhotoKey, string | null>,
  ) => {
    const { error } = await supabase
      .from("clubs")
      .update({
        sponsor_urls: nextSponsors,
        sponsors_enabled: nextShow,
        photo_matchday: nextPhotos.photo_matchday,
        photo_goal: nextPhotos.photo_goal,
        photo_final: nextPhotos.photo_final,
      })
      .eq("id", clubId);

    if (error) {
      toast({ title: "Fehler", description: "Änderungen konnten nicht gespeichert werden.", variant: "destructive" });
      return;
    }

    const { error: hookError } = await supabase.functions.invoke("club-media-sync", {
      body: {
        accountId: userId,
        clubName,
        sponsors: nextShow ? nextSponsors : "none",
        photoMatchday: nextPhotos.photo_matchday,
        photoGoal: nextPhotos.photo_goal,
        photoFinal: nextPhotos.photo_final,
      },
    });

    if (hookError) {
      toast({
        title: "Gespeichert, aber nicht übertragen",
        description: "Die Daten sind gesichert, konnten aber nicht an die Grafik-Erstellung gemeldet werden.",
        variant: "destructive",
      });
      return;
    }

    notifySaved();
  };

  const uploadFile = async (file: Blob, folder: "sponsoren" | "fotos", isPng: boolean) => {
    const ext = isPng ? "png" : "jpg";
    const path = `${clubId}/${folder}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { upsert: false, contentType: isPng ? "image/png" : "image/jpeg" });
    if (error) throw new Error(`Upload fehlgeschlagen: ${error.message}`);
    const url = publicUrl(path);
    if (!(await isReachable(url))) {
      await supabase.storage.from(BUCKET).remove([path]);
      throw new Error("Die Datei ist nach dem Hochladen nicht erreichbar.");
    }
    return url;
  };

  const handleSponsorFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const list = Array.from(files);
    const free = MAX_SPONSORS - sponsors.length;
    if (free <= 0) {
      toast({ title: "Maximal 6 Logos", description: "Lösche zuerst ein Logo.", variant: "destructive" });
      return;
    }
    const valid = list.slice(0, free).filter((f) => {
      if (!SPONSOR_TYPES.includes(f.type)) {
        toast({ title: "Format nicht unterstützt", description: `${f.name}: nur PNG oder JPG.`, variant: "destructive" });
        return false;
      }
      if (f.size > SPONSOR_MAX_BYTES) {
        toast({ title: "Datei zu groß", description: `${f.name}: maximal 5 MB.`, variant: "destructive" });
        return false;
      }
      return true;
    });
    if (!valid.length) return;

    setBusy(true);
    const uploaded: string[] = [];
    try {
      for (const file of valid) {
        const isPng = file.type === "image/png";
        const blob = await resizeImage(file, 1000, isPng);
        uploaded.push(await uploadFile(blob, "sponsoren", isPng));
      }
    } catch (e) {
      toast({
        title: "Logo nicht hochgeladen",
        description: e instanceof Error ? e.message : "Unbekannter Fehler.",
        variant: "destructive",
      });
    }
    if (uploaded.length) {
      const next = [...sponsors, ...uploaded];
      setSponsors(next);
      await persist(next, showSponsors, photoUrls);
    }
    setBusy(false);
    if (sponsorInput.current) sponsorInput.current.value = "";
  };

  const moveSponsor = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= sponsors.length) return;
    const next = [...sponsors];
    [next[index], next[target]] = [next[target], next[index]];
    setSponsors(next);
    setBusy(true);
    await persist(next, showSponsors, photoUrls);
    setBusy(false);
  };

  const removeSponsor = async (index: number) => {
    const next = sponsors.filter((_, i) => i !== index);
    setSponsors(next);
    setBusy(true);
    await persist(next, showSponsors, photoUrls);
    setBusy(false);
  };

  const toggleSponsors = async (value: boolean) => {
    setShowSponsors(value);
    setBusy(true);
    await persist(sponsors, value, photoUrls);
    setBusy(false);
  };

  const handlePhoto = async (key: PhotoKey, file: File | null) => {
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast({ title: "Format nicht unterstützt", description: "Bitte JPG oder PNG hochladen.", variant: "destructive" });
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      toast({ title: "Datei zu groß", description: "Maximal 5 MB pro Foto.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const url = await uploadFile(file, "fotos", file.type === "image/png");
      const next = { ...photoUrls, [key]: url };
      setPhotoUrls(next);
      await persist(sponsors, showSponsors, next);
    } catch (e) {
      toast({
        title: "Foto nicht hochgeladen",
        description: e instanceof Error ? e.message : "Unbekannter Fehler.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (key: PhotoKey) => {
    const next = { ...photoUrls, [key]: null };
    setPhotoUrls(next);
    setBusy(true);
    await persist(sponsors, showSponsors, next);
    setBusy(false);
  };

  return (
    <>
      {/* Sponsoren */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div>
          <h2 className="font-display text-lg font-bold uppercase tracking-tight text-foreground">Sponsoren</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            PNG mit transparentem Hintergrund sieht am besten aus. Die Logos erscheinen unten auf euren Grafiken.
          </p>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
          <Label htmlFor="sponsors-toggle" className="text-sm font-medium text-foreground">
            Sponsorenleiste anzeigen
          </Label>
          <Switch id="sponsors-toggle" checked={showSponsors} disabled={busy} onCheckedChange={toggleSponsors} />
        </div>

        {sponsors.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {sponsors.map((url, index) => (
              <div key={url} className="rounded-lg border border-border bg-background p-2">
                <div className="flex h-20 items-center justify-center overflow-hidden rounded bg-white">
                  {broken.has(url) ? (
                    <p className="flex items-center gap-1.5 px-2 text-center text-xs font-medium text-destructive">
                      <AlertTriangle className="h-4 w-4 shrink-0" /> Logo nicht erreichbar – bitte löschen und neu hochladen
                    </p>
                  ) : (
                    <img
                      src={url}
                      alt={`Sponsor ${index + 1}`}
                      className="max-h-16 max-w-full object-contain"
                      onError={() => setBroken((s) => new Set(s).add(url))}
                    />
                  )}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      aria-label="Nach vorne"
                      disabled={busy || index === 0}
                      onClick={() => moveSponsor(index, -1)}
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      aria-label="Nach hinten"
                      disabled={busy || index === sponsors.length - 1}
                      onClick={() => moveSponsor(index, 1)}
                    >
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    aria-label="Logo löschen"
                    disabled={busy}
                    onClick={() => removeSponsor(index)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <input
            ref={sponsorInput}
            type="file"
            accept="image/png,image/jpeg"
            multiple
            className="hidden"
            onChange={(e) => handleSponsorFiles(e.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            disabled={busy || sponsors.length >= MAX_SPONSORS}
            onClick={() => sponsorInput.current?.click()}
          >
            <Upload className="h-4 w-4" />
            Logo hochladen
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Bis zu 6 Logos, je maximal 5 MB (PNG oder JPG). Große Bilder werden auf 1000 px Breite verkleinert. Die Reihenfolge hier ist die Reihenfolge auf den Grafiken.
          </p>
        </div>
      </div>

      {/* Eure Fotos */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div>
          <h2 className="font-display text-lg font-bold uppercase tracking-tight text-foreground">Eure Fotos</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Quer-Format, mindestens 1200 px breit. Ohne eigenes Foto nutzen wir ein neutrales Bild.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          {PHOTO_SLOTS.map((slot) => (
            <div key={slot.key} className="rounded-lg border border-border bg-background p-3">
              <p className="text-sm font-bold text-foreground">{slot.title}</p>
              <p className="text-xs text-muted-foreground">{slot.hint}</p>

              <div className="mt-3 flex aspect-video items-center justify-center overflow-hidden rounded bg-muted">
                {photoUrls[slot.key] && broken.has(photoUrls[slot.key] as string) ? (
                  <p className="flex items-center gap-1.5 px-2 text-center text-xs font-medium text-destructive">
                    <AlertTriangle className="h-4 w-4 shrink-0" /> Foto nicht erreichbar – bitte neu hochladen
                  </p>
                ) : photoUrls[slot.key] ? (
                  <img src={photoUrls[slot.key] as string} alt={slot.title} className="h-full w-full object-cover" />
                ) : (
                  <ImagePlus className="h-6 w-6 text-muted-foreground" />
                )}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    disabled={busy}
                    onChange={(e) => handlePhoto(slot.key, e.target.files?.[0] ?? null)}
                  />
                  <span className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted">
                    <Upload className="h-3.5 w-3.5" />
                    {photoUrls[slot.key] ? "Ersetzen" : "Foto wählen"}
                  </span>
                </label>
                {photoUrls[slot.key] ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 text-destructive"
                    disabled={busy}
                    onClick={() => removePhoto(slot.key)}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Entfernen
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Je Foto maximal 5 MB (JPG oder PNG).</p>
      </div>
    </>
  );
}
